import {
  useState, useMemo, useRef, useEffect,
  ChangeEvent, MouseEvent,
} from "react";
import { DeleteModal, Invoice, InvoiceStatus, SortDir, SortField, StatusFilter, ToastState, ViewModal } from "./types";
import { INVOICES_RAW, PAYMENT_METHOD_LABELS, STATUS_CFG } from "./constants";
import { daysUntil, fmtCurrency, fmtDate, highlight } from "./helpers";
import { IconBtn } from "../../components/common/UI/Button/IconBtn";
import { SortIcon } from "../../components/common/UI/Icons/SortIcon";
import StatusBadge from "../../components/common/UI/Badge/StatusBadge";

// ─── Delete modal ─────────────────────────────────────────────────────────────

function DelModal({ modal, onConfirm, onCancel }: { modal: DeleteModal; onConfirm: () => void; onCancel: () => void }) {
  if (!modal.open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onCancel}>
      <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{ background: "#1a1917", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 28, width: 380, maxWidth: "90vw", animation: "modalIn 0.2s ease" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(224,82,82,0.12)", border: "1px solid rgba(224,82,82,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, marginBottom: 16 }}>🗑</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>Delete invoice?</div>
        <div style={{ fontSize: 14, color: "var(--text3)", lineHeight: 1.6, marginBottom: 24 }}><strong style={{ color: "var(--text2)" }}>{modal.number}</strong> will be permanently deleted.</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(224,82,82,0.35)", background: "rgba(224,82,82,0.12)", color: "#e05252", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Invoice detail / print modal ─────────────────────────────────────────────

function InvoiceModal({ modal, onClose, onStatusChange }: {
  modal: ViewModal;
  onClose: () => void;
  onStatusChange: (id: string, status: InvoiceStatus) => void;
}) {
  if (!modal.open || !modal.invoice) return null;
  const inv = modal.invoice;
  const overdue = inv.status === "overdue";
  const daysLeft = daysUntil(inv.dueDate);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()} style={{
        background: "#1a1917", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16,
        width: 740, maxWidth: "95vw", maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column",
        animation: "modalIn 0.2s ease",
      }}>
        {/* Modal header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text)" }}>{inv.invoiceNumber}</div>
              <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{inv.billTo.companyName} · {fmtDate(inv.issueDate)}</div>
            </div>
            <StatusBadge status={inv.status} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => window.print()} style={{ fontSize: 12, padding: "7px 14px", borderRadius: 7, border: "1px solid var(--border)", background: "rgba(255,255,255,0.04)", color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
              Print
            </button>
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: "var(--text3)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>×</button>
          </div>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* Invoice document */}
          <div style={{ padding: "24px 28px" }}>

            {/* Company + client */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--or)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>From</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>Aurel Global Ltd.</div>
                <div style={{ fontSize: 13, color: "var(--text3)", lineHeight: 1.7 }}>
                  14 Precision Way, Birmingham B1 2JP<br />
                  United Kingdom · VAT: GB123456789<br />
                  accounts@auralglobal.com
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--or)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>Bill To</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", marginBottom: 2 }}>{inv.billTo.companyName}</div>
                <div style={{ fontSize: 13, color: "var(--text3)", lineHeight: 1.7 }}>
                  {inv.billTo.contactName}<br />
                  {inv.billTo.address}<br />
                  {inv.billTo.country}{inv.billTo.vatNumber ? ` · VAT: ${inv.billTo.vatNumber}` : ""}<br />
                  {inv.billTo.email}
                </div>
              </div>
            </div>

            {/* Invoice meta */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 24, background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 10, padding: 16 }}>
              {[
                { label: "Invoice No.", value: inv.invoiceNumber },
                { label: "Issue Date", value: fmtDate(inv.issueDate) },
                { label: "Due Date", value: fmtDate(inv.dueDate) + (overdue ? " ⚠" : daysLeft > 0 ? ` (${daysLeft}d)` : "") },
                { label: "Payment Terms", value: inv.paymentTerms },
              ].map((r) => (
                <div key={r.label}>
                  <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 3 }}>{r.label}</div>
                  <div style={{ fontSize: 13, color: r.label === "Due Date" && overdue ? "#e05252" : "var(--text2)", fontWeight: 500 }}>{r.value}</div>
                </div>
              ))}
            </div>

            {/* Line items */}
            <div style={{ marginBottom: 20 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
                    {["Description", "SKU", "Qty", "Unit Price", "Disc.", "Tax", "Line Total"].map((h) => (
                      <th key={h} style={{ padding: "9px 12px", textAlign: h === "Qty" || h === "Disc." || h === "Tax" || h === "Line Total" ? "right" : "left", fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", borderBottom: "1px solid var(--border)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inv.lineItems.map((item, i) => {
                    const gross = item.qty * item.unitPrice;
                    const disc = gross * (item.discount / 100);
                    const net = gross - disc;
                    const tax = net * (item.taxRate / 100);
                    const lineTotal = net + tax;
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text)", fontWeight: 500 }}>{item.description}</td>
                        <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--text3)", fontFamily: "monospace" }}>{item.sku}</td>
                        <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text2)", textAlign: "right" }}>{item.qty}</td>
                        <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text2)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(item.unitPrice, inv.currency)}</td>
                        <td style={{ padding: "10px 12px", fontSize: 13, color: item.discount > 0 ? "#f5a623" : "var(--text3)", textAlign: "right" }}>{item.discount > 0 ? `-${item.discount}%` : "—"}</td>
                        <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text3)", textAlign: "right" }}>{item.taxRate > 0 ? `${item.taxRate}%` : "—"}</td>
                        <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text)", textAlign: "right", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(lineTotal, inv.currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
              <div style={{ width: 280, display: "flex", flexDirection: "column", gap: 6 }}>
                {[
                  { label: "Subtotal", value: inv.subtotal, muted: true },
                  { label: "Discount", value: -inv.totalDiscount, muted: true, hide: inv.totalDiscount === 0 },
                  { label: "Tax", value: inv.totalTax, muted: true, hide: inv.totalTax === 0 },
                ].filter((r) => !r.hide).map((r) => (
                  <div key={r.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: r.label === "Discount" ? "#f5a623" : "var(--text3)" }}>
                    <span>{r.label}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(r.value, inv.currency)}</span>
                  </div>
                ))}
                <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 600, color: "var(--text)" }}>
                  <span>Total</span>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(inv.total, inv.currency)}</span>
                </div>
                {inv.amountPaid > 0 && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6ddc6d" }}>
                      <span>Amount paid</span>
                      <span style={{ fontVariantNumeric: "tabular-nums" }}>−{fmtCurrency(inv.amountPaid, inv.currency)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 600, color: inv.amountDue > 0 ? "#e05252" : "#6ddc6d" }}>
                      <span>Amount due</span>
                      <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(inv.amountDue, inv.currency)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Payment info + notes */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
              {inv.paidDate && (
                <div style={{ background: "rgba(109,220,109,0.06)", border: "1px solid rgba(109,220,109,0.2)", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, color: "#6ddc6d", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 3 }}>Payment Received</div>
                  <div style={{ fontSize: 13, color: "var(--text2)" }}>{fmtDate(inv.paidDate)} via {inv.paymentMethod ? PAYMENT_METHOD_LABELS[inv.paymentMethod] : "—"}</div>
                </div>
              )}
              {inv.quoteRef && (
                <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 3 }}>Linked Quote</div>
                  <div style={{ fontSize: 13, color: "var(--or)", fontFamily: "monospace" }}>{inv.quoteRef}</div>
                </div>
              )}
            </div>

            {inv.notes && (
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", marginBottom: 20 }}>
                <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: 4 }}>Notes</div>
                <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>{inv.notes}</div>
              </div>
            )}

            {/* Status update */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>Update Status</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(Object.keys(STATUS_CFG) as InvoiceStatus[]).map((s) => {
                  const cfg = STATUS_CFG[s];
                  const isActive = inv.status === s;
                  return (
                    <button key={s} onClick={() => { onStatusChange(inv.id, s); onClose(); }} style={{
                      fontSize: 12, padding: "6px 12px", borderRadius: 7,
                      border: `1px solid ${isActive ? cfg.border : "var(--border)"}`,
                      background: isActive ? cfg.bg : "transparent",
                      color: isActive ? cfg.color : "var(--text3)",
                      cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s",
                    }}>{cfg.label}</button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const PAGE_SIZES = [10, 20, 50];

export default function AllInvoices() {
  const [invoices, setInvoices] = useState<Invoice[]>(INVOICES_RAW);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortField, setSortField] = useState<SortField>("issueDate");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [viewModal, setViewModal] = useState<ViewModal>({ open: false, invoice: null });
  const [deleteModal, setDeleteModal] = useState<DeleteModal>({ open: false, id: null, number: "" });
  const [toast, setToast] = useState<ToastState>({ visible: false, message: "", type: "success" });
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (message: string, type: ToastState["type"] = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ visible: true, message, type });
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  };

  const filtered = useMemo(() => {
    let list = [...invoices];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((inv) => inv.invoiceNumber.toLowerCase().includes(q) || inv.billTo.companyName.toLowerCase().includes(q) || inv.billTo.country.toLowerCase().includes(q));
    }
    if (statusFilter !== "all") list = list.filter((inv) => inv.status === statusFilter);
    list.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      if (sortField === "billTo") { va = a.billTo.companyName; vb = b.billTo.companyName; }
      else if (sortField === "total") { va = a.total; vb = b.total; }
      else if (sortField === "amountDue") { va = a.amountDue; vb = b.amountDue; }
      else { va = a[sortField as keyof Invoice] as string ?? ""; vb = b[sortField as keyof Invoice] as string ?? ""; }
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [invoices, search, statusFilter, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageSlice = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

  const allSelected = pageSlice.length > 0 && pageSlice.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    const next = new Set(selected);
    allSelected ? pageSlice.forEach((r) => next.delete(r.id)) : pageSlice.forEach((r) => next.add(r.id));
    setSelected(next);
  };
  const toggle = (id: string) => {
    const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); setSelected(next);
  };

  const toggleSort = (f: SortField) => {
    if (sortField === f) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(f); setSortDir("desc"); }
  };

  const handleStatusChange = (id: string, status: InvoiceStatus) => {
    setInvoices((prev) => prev.map((inv) => inv.id === id ? { ...inv, status, paidDate: status === "paid" ? new Date().toISOString() : inv.paidDate, amountPaid: status === "paid" ? inv.total : inv.amountPaid, amountDue: status === "paid" ? 0 : inv.amountDue } : inv));
    showToast(`Invoice status updated to "${STATUS_CFG[status].label}"`);
  };

  const handleDelete = () => {
    if (!deleteModal.id) return;
    setInvoices((prev) => prev.filter((inv) => inv.id !== deleteModal.id));
    setSelected((prev) => { const n = new Set(prev); n.delete(deleteModal.id!); return n; });
    setDeleteModal({ open: false, id: null, number: "" });
    showToast("Invoice deleted");
  };

  const stats = useMemo(() => {
    const totalRevenue = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);
    const totalOutstanding = invoices.filter((i) => ["sent", "viewed", "overdue"].includes(i.status)).reduce((s, i) => s + i.amountDue, 0);
    const overdueCount = invoices.filter((i) => i.status === "overdue").length;
    const draftCount = invoices.filter((i) => i.status === "draft").length;
    const paidCount = invoices.filter((i) => i.status === "paid").length;
    return { totalRevenue, totalOutstanding, overdueCount, draftCount, paidCount, total: invoices.length };
  }, [invoices]);

  const pageNums = (): (number | "…")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safePage <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
    if (safePage >= totalPages - 3) return [1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages];
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');
        // *, *::before, *::after { box-sizing: border-box; }
        // :root {
        //   --or:#BF3D0C; --or2:#E8593C; --or-dim:rgba(191,61,12,0.10);
        //   --text:#e8e6de; --text2:#b4b2a9; --text3:#6a6a65;
        //   --border:rgba(255,255,255,0.08); --card:rgba(255,255,255,0.025);
        //   --row-hover:rgba(255,255,255,0.028); --input-bg:rgba(255,255,255,0.035);
        //   --section-header:rgba(255,255,255,0.02); --surface:#111110;
        // }
        body { margin:0; font-family:'DM Sans',sans-serif; }
        input::placeholder { color:var(--text3); }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.08); border-radius:4px; }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toastIn     { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes modalIn     { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }
        .iv-row { transition:background 0.12s; }
        .iv-row:hover { background:var(--row-hover)!important; }
        .iv-th { user-select:none; cursor:pointer; }
        .iv-th:hover { color:var(--text)!important; }
        .iv-pg:hover:not(:disabled) { background:var(--or-dim)!important; color:var(--or)!important; border-color:rgba(191,61,12,0.3)!important; }
        .iv-filter:hover { background:rgba(255,255,255,0.05)!important; }
        @media (max-width: 768px) { .invoice-stats { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width: 480px) { .invoice-stats { grid-template-columns: repeat(1,1fr) !important; } }
        @media print { body { background:#fff!important; } }
      `}</style>

      {toast.visible && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 600, background: "#1a2e1a", border: "1px solid rgba(90,200,90,0.25)", borderRadius: 10, padding: "12px 18px", display: "flex", alignItems: "center", gap: 10, animation: "toastIn 0.25s ease", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
          <span style={{ fontSize: 14, color: "#9be09b" }}>✓&nbsp;&nbsp;{toast.message}</span>
        </div>
      )}

      <InvoiceModal modal={viewModal} onClose={() => setViewModal({ open: false, invoice: null })} onStatusChange={handleStatusChange} />
      <DelModal modal={deleteModal} onConfirm={handleDelete} onCancel={() => setDeleteModal({ open: false, id: null, number: "" })} />

      <div style={{ minHeight: "100vh", padding: "32px 28px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", animation: "fadeSlideIn 0.3s ease" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>Finance</div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text)", letterSpacing: "-0.4px" }}>Invoices</h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text3)" }}>
                {invoices.length} invoices · {stats.overdueCount > 0 && <span style={{ color: "#e05252" }}>{stats.overdueCount} overdue</span>}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 14 }}>↓</span> Export
              </button>
              {/* <button style={{ display:"flex",alignItems:"center",gap:7,padding:"10px 18px",borderRadius:9,border:"1px solid rgba(191,61,12,0.35)",background:"linear-gradient(135deg,#BF3D0C,#E8593C)",color:"#fff",fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",boxShadow:"0 2px 12px rgba(191,61,12,0.25)" }}>
                <span style={{ fontSize:16,lineHeight:1 }}>+</span> New Invoice
              </button> */}
            </div>
          </div>

          {/* Stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 }} className="invoice-stats">
            {[
              { label: "Total", value: String(stats.total), color: "var(--text2)", sub: "all invoices" },
              { label: "Revenue", value: fmtCurrency(stats.totalRevenue), color: "#6ddc6d", sub: "paid invoices" },
              { label: "Outstanding", value: fmtCurrency(stats.totalOutstanding), color: "#f5a623", sub: "awaiting payment" },
              { label: "Overdue", value: String(stats.overdueCount), color: "#e05252", sub: "past due date" },
              { label: "Drafts", value: String(stats.draftCount), color: "var(--text3)", sub: "not yet sent" },
            ].map((s) => (
              <div key={s.label} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 18px" }}>
                <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: typeof s.value === "string" && s.value.startsWith("$") && s.value.length > 8 ? 15 : 22, fontWeight: 600, color: s.color, lineHeight: 1, marginBottom: 3 }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "var(--text3)" }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Table panel */}
          <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>

            {/* Toolbar */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: "1 1 200px", minWidth: 160 }}>
                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--text3)", fontSize: 14, pointerEvents: "none" }}>⌕</span>
                <input value={search} onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder="Search invoice, company…"
                  style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 30px 8px 32px", fontSize: 13, color: "var(--text)", outline: "none", fontFamily: "'DM Sans',sans-serif" }} />
                {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text3)", cursor: "pointer", fontSize: 14 }}>×</button>}
              </div>

              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {(["all", "draft", "sent", "viewed", "paid", "overdue", "cancelled"] as StatusFilter[]).map((s) => {
                  const active = statusFilter === s;
                  const cfg = s !== "all" ? STATUS_CFG[s] : null;
                  return (
                    <button key={s} className="iv-filter" onClick={() => setStatusFilter(s)} style={{
                      fontSize: 12, padding: "5px 11px", borderRadius: 7, cursor: "pointer",
                      border: `1px solid ${active ? (cfg?.border ?? "rgba(191,61,12,0.3)") : "var(--border)"}`,
                      background: active ? (cfg?.bg ?? "var(--or-dim)") : "transparent",
                      color: active ? (cfg?.color ?? "var(--or)") : "var(--text3)",
                      fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s",
                    }}>{s === "all" ? "All" : STATUS_CFG[s as InvoiceStatus].label}</button>
                  );
                })}
              </div>

              {someSelected && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "var(--text3)" }}>{selected.size} selected</span>
                  <button onClick={() => { selected.forEach((id) => handleStatusChange(id, "sent")); showToast(`${selected.size} invoice${selected.size > 1 ? "s" : ""} marked as Sent`); setSelected(new Set()); }} style={{ fontSize: 12, padding: "6px 11px", borderRadius: 7, border: "1px solid rgba(138,180,248,0.3)", background: "rgba(138,180,248,0.08)", color: "#8ab4f8", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Mark sent</button>
                  <button onClick={() => { selected.forEach((id) => handleStatusChange(id, "paid")); showToast(`${selected.size} invoice${selected.size > 1 ? "s" : ""} marked as Paid`); setSelected(new Set()); }} style={{ fontSize: 12, padding: "6px 11px", borderRadius: 7, border: "1px solid rgba(109,220,109,0.3)", background: "rgba(109,220,109,0.08)", color: "#6ddc6d", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Mark paid</button>
                </div>
              )}

              <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: someSelected ? 0 : "auto", whiteSpace: "nowrap" }}>{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 960 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                    <th style={{ width: 44, padding: "11px 16px", textAlign: "center", borderBottom: "1px solid var(--border)" }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                    </th>
                    {([
                      { field: "invoiceNumber" as SortField, label: "Invoice", w: 120 },
                      { field: "billTo" as SortField, label: "Client", w: "auto" },
                      { field: "status" as SortField, label: "Status", w: 120 },
                      { field: "issueDate" as SortField, label: "Issued", w: 100 },
                      { field: "dueDate" as SortField, label: "Due", w: 100 },
                      { field: "total" as SortField, label: "Total", w: 110 },
                      { field: "amountDue" as SortField, label: "Amt Due", w: 110 },
                    ]).map((col) => (
                      <th key={col.field} className="iv-th" onClick={() => toggleSort(col.field)} style={{ width: col.w, padding: "11px 14px", textAlign: "left", borderBottom: "1px solid var(--border)", fontSize: 11, fontWeight: 600, color: sortField === col.field ? "var(--or)" : "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px", whiteSpace: "nowrap" }}>
                        {col.label}<SortIcon active={sortField === col.field} dir={sortDir} />
                      </th>
                    ))}
                    <th style={{ width: 100, padding: "11px 14px", borderBottom: "1px solid var(--border)", fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.8px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageSlice.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding: "56px 24px", textAlign: "center", color: "var(--text3)", fontSize: 14 }}>No invoices found</td></tr>
                  ) : pageSlice.map((inv, idx) => {
                    const overdue = inv.status === "overdue";
                    const daysLeft = daysUntil(inv.dueDate);
                    return (
                      <tr key={inv.id} className="iv-row" style={{
                        background: selected.has(inv.id) ? "rgba(191,61,12,0.06)" : "transparent",
                        borderBottom: idx < pageSlice.length - 1 ? "1px solid var(--border)" : "none",
                        animation: `fadeSlideIn 0.2s ease ${idx * 0.025}s both`,
                      }}>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <input type="checkbox" checked={selected.has(inv.id)} onChange={() => toggle(inv.id)} style={{ accentColor: "var(--or)", cursor: "pointer", width: 14, height: 14 }} />
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--or)", fontFamily: "monospace" }}>{inv.invoiceNumber}</span>
                          {inv.quoteRef && <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "monospace", marginTop: 2 }}>← {inv.quoteRef}</div>}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", marginBottom: 1 }}>{highlight(inv.billTo.companyName, search)}</div>
                          <div style={{ fontSize: 12, color: "var(--text3)" }}>{inv.billTo.country}</div>
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <StatusBadge status={inv.status} />
                            <select value={inv.status} onChange={(e: ChangeEvent<HTMLSelectElement>) => handleStatusChange(inv.id, e.target.value as InvoiceStatus)} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}>
                              {(Object.keys(STATUS_CFG) as InvoiceStatus[]).map((s) => <option key={s} value={s}>{STATUS_CFG[s].label}</option>)}
                            </select>
                          </div>
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: 13, color: "var(--text3)", whiteSpace: "nowrap" }}>{fmtDate(inv.issueDate)}</td>
                        <td style={{ padding: "12px 14px", fontSize: 13, whiteSpace: "nowrap", color: overdue ? "#e05252" : daysLeft <= 7 && daysLeft > 0 ? "#f5a623" : "var(--text3)" }}>
                          {fmtDate(inv.dueDate)}
                          {overdue && <div style={{ fontSize: 10, color: "#e05252", fontWeight: 600 }}>OVERDUE</div>}
                          {!overdue && daysLeft <= 7 && daysLeft > 0 && <div style={{ fontSize: 10, color: "#f5a623", fontWeight: 600 }}>{daysLeft}d left</div>}
                        </td>
                        <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 500, color: "var(--text)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(inv.total, inv.currency)}</td>
                        <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 600, color: inv.amountDue > 0 ? (overdue ? "#e05252" : "#f5a623") : "#6ddc6d", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                          {inv.amountDue === 0 ? "—" : fmtCurrency(inv.amountDue, inv.currency)}
                        </td>
                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <IconBtn title="View invoice" onClick={() => setViewModal({ open: true, invoice: inv })} accent>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                            </IconBtn>
                            <IconBtn title="Download PDF" onClick={() => showToast("Preparing PDF download…", "info")}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                            </IconBtn>
                            <IconBtn title="Delete" danger onClick={() => setDeleteModal({ open: true, id: inv.id, number: inv.invoiceNumber })}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                            </IconBtn>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "var(--text3)" }}>Rows per page</span>
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "rgba(255,255,255,0.04)", color: "var(--text2)", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", outline: "none" }}>
                  {PAGE_SIZES.map((n) => <option key={n} value={n} style={{ background: "#1a1917" }}>{n}</option>)}
                </select>
              </div>
              <span style={{ fontSize: 12, color: "var(--text3)" }}>
                {filtered.length === 0 ? "0" : `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)}`} of {filtered.length}
              </span>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {[{ l: "«", a: () => setPage(1), d: safePage === 1 }, { l: "‹", a: () => setPage(p => Math.max(1, p - 1)), d: safePage === 1 }].map((b) => (
                  <button key={b.l} className="iv-pg" onClick={b.a} disabled={b.d} style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: b.d ? "var(--text3)" : "var(--text2)", cursor: b.d ? "not-allowed" : "pointer", fontSize: 14, opacity: b.d ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>{b.l}</button>
                ))}
                {pageNums().map((n, i) => n === "…" ? <span key={`e${i}`} style={{ width: 30, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>…</span> : (
                  <button key={n} className="iv-pg" onClick={() => setPage(n as number)} style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${safePage === n ? "rgba(191,61,12,0.35)" : "var(--border)"}`, background: safePage === n ? "var(--or-dim)" : "transparent", color: safePage === n ? "var(--or)" : "var(--text2)", cursor: "pointer", fontSize: 13, fontWeight: safePage === n ? 600 : 400, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>{n}</button>
                ))}
                {[{ l: "›", a: () => setPage(p => Math.min(totalPages, p + 1)), d: safePage === totalPages }, { l: "»", a: () => setPage(totalPages), d: safePage === totalPages }].map((b) => (
                  <button key={b.l} className="iv-pg" onClick={b.a} disabled={b.d} style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)", background: "transparent", color: b.d ? "var(--text3)" : "var(--text2)", cursor: b.d ? "not-allowed" : "pointer", fontSize: 14, opacity: b.d ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>{b.l}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}